/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  formatRecordingDateTime,
  formatRecordingTime,
  recordingDateKey,
} from "../utils/recordingDate";
import { accountDisplayName } from "../auth/accountLogin";
import { localUsername } from "../auth/admin/localUsername";
import { logger } from "matrix-js-sdk/lib/logger";

import { Link } from "react-router-dom";
import { type Room, type MatrixClient, UserEvent } from "matrix-js-sdk";
import {
  type FC,
  useCallback,
  type MouseEvent,
  useState,
  useMemo,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { IconButton, Text } from "@vector-im/compound-web";
import { CloseIcon } from "@vector-im/compound-design-tokens/assets/web/icons";
import classNames from "classnames";

import { Avatar, Size } from "../Avatar";
import styles from "./CallList.module.css";
import { getRelativeRoomUrl } from "../utils/matrix";
import { type GroupCallRoom } from "./useGroupCallRooms";
import { useRoomEncryptionSystem } from "../e2ee/sharedKeyManagement";

import { RecordingDateFilter } from "../components/RecordingDateFilter";
import { backendFetch, BackendError } from "../utils/backend";
import { RoomHistoryEntry, type HistoryRoom } from "./RoomHistory";
import {
  ParticipantFilter,
  includesParticipants,
  type HistoryParticipant,
} from "./ParticipantFilter";
import { readHistoryFilters, writeHistoryFilters } from "./historyFilters";

interface CallListProps {
  rooms: GroupCallRoom[];
  client: MatrixClient;
  isOwner?: boolean;
}

const formatDuration = (seconds?: number): string => {
  if (!seconds || !Number.isFinite(seconds)) return "";
  const h = Math.floor(seconds / 3600),
    m = Math.floor((seconds % 3600) / 60),
    sec = Math.floor(seconds % 60);
  const pad = (value: number): string => String(value).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

export const CallList: FC<CallListProps> = (props) => (
  <CallListContent
    key={`${props.client.getHomeserverUrl?.()}:${props.client.getUserId()}`}
    {...props}
  />
);

const CallListContent: FC<CallListProps> = ({
  rooms,
  client,
  isOwner = false,
}) => {
  const { t } = useTranslation();
  const [recordings, setRecordings] = useState<any[]>([]);
  const [history, setHistory] = useState<HistoryRoom[] | null>(null);
  const [personalRoomIds, setPersonalRoomIds] = useState<Set<string>>(
    new Set(),
  );
  const [loadError, setLoadError] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  const [, refreshProfileNames] = useState(0);
  useEffect(() => {
    const changed = (): void => refreshProfileNames(value => value + 1);
    client.on?.(UserEvent.DisplayName, changed);
    return () => { client.removeListener?.(UserEvent.DisplayName, changed); };
  }, [client]);
  const storageKey = `call-history:${client.getHomeserverUrl?.() ?? ""}:${client.getUserId()}`;
  const [initial] = useState(() => readHistoryFilters(storageKey));
  const [tab, setTab] = useState(initial.scope);
  const [onlyRecorded, setOnlyRecorded] = useState(initial.onlyRecorded);
  const [date, setDate] = useState(initial.date);
  const [query, setQuery] = useState(initial.query);
  const [oldest, setOldest] = useState(initial.oldest);
  const [selectedParticipants, setSelectedParticipants] = useState(
    initial.participants,
  );
  const others = isOwner && tab === "other_calls";
  const all = isOwner && (tab === "all_calls" || others);
  useEffect(() => {
    writeHistoryFilters(storageKey, {
      scope: tab,
      onlyRecorded,
      date,
      query,
      oldest,
      participants: selectedParticipants,
    });
  }, [
    storageKey,
    isOwner,
    tab,
    onlyRecorded,
    date,
    query,
    oldest,
    selectedParticipants,
  ]);
  useEffect(() => {
    const controller = new AbortController();
    setHistory(null);
    setRecordings([]);
    setLoadError(false);
    const suffix = all ? "?scope=all" : "";
    let nextRefreshAt = 0;
    const load = (): void => {
      if (busy || controller.signal.aborted || Date.now() < nextRefreshAt) return;
      nextRefreshAt = Date.now() + 10000;
      busy = true;
      void Promise.all([
        backendFetch(client, "/api/rooms" + suffix, {
          signal: controller.signal,
        }).then(async (r) => r.json()),
        backendFetch(client, "/api/recordings" + suffix, {
          signal: controller.signal,
        }).then(async (r) => r.json()),
        ...(others
          ? [
              backendFetch(client, "/api/rooms", {
                signal: controller.signal,
              }).then(async (r) => r.json()),
            ]
          : []),
      ])
        .then(([saved, records, personalRooms]) => {
          if (
            !Array.isArray(saved) ||
            !Array.isArray(records) ||
            (others && !Array.isArray(personalRooms))
          )
            throw Error("Invalid room history");
          if (!controller.signal.aborted) {
            const ownRooms = new Set<string>(
              (personalRooms ?? []).map(
                (room: HistoryRoom) => room.matrixRoomId,
              ),
            );
            setPersonalRoomIds(ownRooms);
            setHistory(saved);
            setRecordings(records);
            setLoadError(false);
          }
        })
        .catch((error) => {
          if (!controller.signal.aborted) {
            if (error instanceof BackendError && error.status === 429)
              nextRefreshAt = Date.now() + 60000;
            setLoadError(true);
            logger.error("Could not load history", error);
          }
        })
        .finally(() => {
          busy = false;
        });
    };
    let busy = false;
    load();
    const refresh = (): void => {
      if (document.visibilityState !== "hidden") load();
    };
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [client, all, others, retryVersion]);
  // Wait for backend history before mounting Matrix-only tiles: each tile
  // needs a TTL check, which is redundant for rooms the history already covers.
  const previousRooms = all || history === null
    ? []
    : history
      ? rooms.filter(
          ({ room }) =>
            !history.some((saved) => saved.matrixRoomId === room.roomId),
        )
      : rooms;
  const recordsFor = (id: string): any[] =>
    recordings.filter((rec) => rec.matrixRoomId === id || rec.roomId === id);
  const matches = (name: string, timestamp: number): boolean =>
    (!query || name.toLocaleLowerCase().includes(query.toLocaleLowerCase())) &&
    (!date || recordingDateKey(timestamp) === date);
  const participantsFor = (entry: {
    saved?: HistoryRoom;
    previous?: GroupCallRoom;
  }): HistoryParticipant[] =>
    entry.saved?.participants ??
    entry.previous?.room
      .getMembers()
      .filter(
        (member) =>
          member.membership === "join" || member.membership === "leave",
      )
      .map((member) => ({ userId: member.userId, name: member.name })) ??
    [];
  const knownParticipants = [
    ...[...(history ?? [])]
      .filter((room) => !others || !personalRoomIds.has(room.matrixRoomId))
      .sort((a, b) => Math.max(b.lastLeftAt ?? 0, b.createdAt) - Math.max(a.lastLeftAt ?? 0, a.createdAt))
      .flatMap((room) => room.participants),
    ...previousRooms.flatMap((previous) => participantsFor({ previous })),
  ];
  const participantsById = new Map<string, HistoryParticipant>();
  for (const person of knownParticipants) {
    // History is newest first. Never replace a recent name with an old room's
    // snapshot, and never treat a renamed user as a second identity.
    if (!participantsById.has(person.userId)) participantsById.set(person.userId, person);
  }
  const participantOptions = Array.from(participantsById.values(), person => {
    const profile = client.getUser?.(person.userId);
    return {
      ...person,
      name: profile ? profile.displayName || localUsername(person.userId) : person.name,
    };
  }).sort((a, b) => (a.name || a.userId).localeCompare(b.name || b.userId));
  const entries = [
    ...(history ?? []).map((saved) => ({
      id: saved.matrixRoomId,
      name: saved.name,
      createdAt: saved.createdAt,
      saved,
      previous: undefined as GroupCallRoom | undefined,
    })),
    ...previousRooms.map((previous) => ({
      id: previous.room.roomId,
      name: previous.roomName,
      createdAt:
        previous.room.currentState
          .getStateEvents("m.room.create", "")
          ?.getTs() ?? 0,
      saved: undefined as HistoryRoom | undefined,
      previous,
    })),
  ]
    .filter(
      (entry) =>
        (!others ||
          (!personalRoomIds.has(entry.id) &&
            !rooms.some(({ room }) => room.roomId === entry.id))) &&
        matches(entry.name, entry.createdAt) &&
        includesParticipants(participantsFor(entry), selectedParticipants) &&
        (!onlyRecorded || recordsFor(entry.id).length > 0),
    )
    .sort((a, b) => (oldest ? 1 : -1) * (a.createdAt - b.createdAt));
  return (
    <section className={styles.historyList}>
      <div
        className={styles.historyTabs}
        aria-label={t("instance.ui.history_sections")}
      >
        {isOwner && (
          <select
            className={styles.historySection}
            aria-label={t("instance.ui.calls_filter")}
            value={tab}
            onChange={(event) => setTab(event.target.value as typeof tab)}
          >
            {(["all", "my", "other"] as const).map((scope) => (
              <option key={scope} value={`${scope}_calls`}>
                {t(`instance.ui.${scope}_calls`)}
              </option>
            ))}
          </select>
        )}
        <ParticipantFilter
          participants={participantOptions}
          selected={selectedParticipants}
          onChange={setSelectedParticipants}
        />
      </div>
      <div className={styles.historyFilters}>
        <div className={styles.historyFilterFields}>
          <input
            type="search"
            className={styles.historyFilter}
            aria-label={t("instance.ui.search_history")}
            placeholder={t("instance.ui.search_history_short")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <RecordingDateFilter
            label={t("instance.ui.filter_date")}
            value={date}
            className={styles.historyFilter}
            onChange={setDate}
          />
        </div>
        <div className={styles.historyFilterOptions}>
          <select
            className={styles.historyFilter}
            aria-label={t("instance.recordings_page.recording_order")}
            value={oldest ? "oldest" : "newest"}
            onChange={(event) => setOldest(event.target.value === "oldest")}
          >
            <option value="newest">{t("instance.ui.newest_first")}</option>
            <option value="oldest">{t("instance.ui.oldest_first")}</option>
          </select>
          <button
            type="button"
            role="switch"
            className={styles.recordingFilter}
            aria-label={t("instance.ui.only_with_recording")}
            aria-checked={onlyRecorded}
            title={t("instance.ui.only_with_recording")}
            onClick={() => setOnlyRecorded((value) => !value)}
          >
            <span className={styles.switchTrack} aria-hidden="true" />
            <span className={styles.recordingFilterLabel}>
              {t("instance.ui.with_recording")}
            </span>
          </button>
        </div>
      </div>
      {loadError && (
        <div role="alert" className={styles.historyError}>
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v6m0 4h.01" />
          </svg>
          <p>{t("instance.ui.history_error")}</p>
          <button
            type="button"
            className={`glass-button ${styles.retryButton}`}
            onClick={() => setRetryVersion((version) => version + 1)}
          >
            {t("instance.ui.history_retry")}
          </button>
        </div>
      )}
      {!loadError && history === null && (
        <p role="status">{t("common.loading")}</p>
      )}
      {entries.map(({ id, saved, previous }) =>
        saved ? (
          <RoomHistoryEntry
            key={id}
            room={saved}
            currentUserId={client.getUserId()}
            recordings={recordsFor(id)}
          />
        ) : previous ? (
          <CallTile
            key={id}
            client={client}
            name={previous.roomName}
            avatarUrl={previous.avatarUrl}
            room={previous.room}
            recordings={recordsFor(id)}
          />
        ) : null,
      )}
      {history !== null && entries.length === 0 && (
        <p>{t("instance.ui.empty_history")}</p>
      )}
    </section>
  );
};

interface CallTileProps {
  name: string;
  avatarUrl: string;
  room: Room;
  client: MatrixClient;
  recordings: any[];
}

const CallTile: FC<CallTileProps> = ({
  name,
  avatarUrl,
  room,
  client,
  recordings,
}) => {
  const { t } = useTranslation();
  const roomEncryptionSystem = useRoomEncryptionSystem(room.roomId);
  const [isLeaving, setIsLeaving] = useState(false);
  const [availableRoomId, setAvailableRoomId] = useState<string | null>(null);
  const canJoin = availableRoomId === room.roomId && !isLeaving;

  useEffect(() => {
    const controller = new AbortController();
    setAvailableRoomId(null);
    // Matrix membership can precede the first media join, so these rooms may
    // not be in personal backend history yet. Check TTL without extending it.
    void backendFetch(
      undefined,
      `/api/rooms/${encodeURIComponent(room.roomId)}/availability`,
      { signal: controller.signal },
    ).then(
      () => {
        if (!controller.signal.aborted) setAvailableRoomId(room.roomId);
      },
      () => {
        /* Keep history visible when joining is unavailable. */
      },
    );
    return () => controller.abort();
  }, [room.roomId]);

  const onRemove = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsLeaving(true);
      client.leave(room.roomId).catch(() => setIsLeaving(false));
    },
    [room, client],
  );

  const createdAtTs = useMemo(() => {
    const createEvent = room.currentState.getStateEvents("m.room.create", "");
    return createEvent ? createEvent.getTs() : null;
  }, [room]);

  const lastLeftTs = useMemo(() => {
    const callMemberEvents = [
      ...room.currentState.getStateEvents("org.matrix.msc3401.call.member"),
      ...room.currentState.getStateEvents("m.call.member"),
      ...room
        .getLiveTimeline()
        .getEvents()
        .filter(
          (e) =>
            e.getType() === "org.matrix.msc3401.call.member" ||
            e.getType() === "m.call.member",
        ),
    ];

    const callDisconnectEvents = callMemberEvents.filter((e) => {
      const content = e.getContent();
      if (!content || Object.keys(content).length === 0) return true;

      const calls =
        content["m.calls"] || content["org.matrix.msc3401.call"] || [];
      const devices = content["devices"] || content["m.devices"] || [];
      return calls.length === 0 && devices.length === 0;
    });

    if (callDisconnectEvents.length === 0) return null;
    return Math.max(...callDisconnectEvents.map((e) => e.getTs()));
  }, [room]);

  const historicalParticipants = useMemo(() => {
    const allMembers = room.getMembers();
    return allMembers.filter(
      (m) => m.membership === "join" || m.membership === "leave",
    );
  }, [room]);

  const participantNames = useMemo(() => {
    if (!historicalParticipants || historicalParticipants.length === 0)
      return "—";
    const currentUserId = client.getUserId();

    return historicalParticipants
      .map((p) => {
        if (p.userId === currentUserId) {
          return t("instance.ui.you");
        }
        const rawName = accountDisplayName(p.userId, p.name);
        if (rawName && rawName.startsWith("@")) {
          return localUsername(p.userId);
        }
        return rawName;
      })
      .join(", ");
  }, [historicalParticipants, client, t]);

  const body = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
        height: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 0,
          }}
        >
          <div style={{ display: "flex", flexShrink: 0, alignItems: "center" }}>
            <Avatar
              id={room.roomId}
              name={name}
              size={Size.MD}
              src={avatarUrl}
            />
          </div>
          <Text
            weight="semibold"
            className={styles.callName}
            style={{
              margin: 0,
              lineHeight: "1.2",
              fontSize: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Text>
        </div>
        <IconButton
          onClick={onRemove}
          disabled={isLeaving}
          aria-label={t("action.remove")}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          paddingTop: "10px",
          borderTop: "1px solid var(--app-border)",
          fontSize: "13px",
          color: "var(--app-muted)",
          textAlign: "left",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{t("instance.ui.created")}</span>
          <span style={{ color: "var(--app-text)" }}>
            {formatRecordingDateTime(createdAtTs)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {t("instance.ui.participants", {
              count: historicalParticipants?.length || 0,
            })}
          </span>
          <span
            style={{
              color: "var(--app-text)",
              maxWidth: "260px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {participantNames}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{t("instance.ui.last_left")}</span>
          <span
            style={{
              color: lastLeftTs ? "var(--app-text)" : "var(--app-subtle)",
            }}
          >
            {lastLeftTs
              ? formatRecordingDateTime(lastLeftTs)
              : t("instance.ui.no_departures")}
          </span>
        </div>

        {canJoin && (
          <span
            style={{
              color: "var(--app-accent)",
              fontWeight: 600,
              marginTop: "6px",
            }}
          >
            {t("instance.ui.join_call")}
          </span>
        )}

        {/* Секция записей (одна или несколько) */}
        {recordings.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              marginTop: "4px",
              paddingTop: "8px",
              borderTop: "1px dashed var(--app-border)",
              width: "100%",
            }}
          >
            <span
              style={{
                color: "var(--app-muted)",
                fontWeight: "600",
                fontSize: "12px",
              }}
            >
              {recordings.length === 1
                ? t("instance.ui.recording")
                : t("instance.ui.recordings", { count: recordings.length })}
            </span>

            {recordings.map((rec, index) => {
              const recordingLink = `/record?v=${encodeURIComponent(rec.id)}&date=${encodeURIComponent(new Date(rec.date).toISOString())}`;

              // Длительность из секунд в MM:SS
              const durationFormatted = formatDuration(rec.duration);
              const displayDuration = durationFormatted
                ? durationFormatted
                : "—";

              const timeFormatted = formatRecordingTime(rec.date);

              return (
                <div
                  key={rec.id || rec.filename}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <span
                    style={{
                      color: "var(--app-muted)",
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {recordings.length > 1 ? (
                      <>
                        <strong style={{ color: "var(--app-text)" }}>
                          #{index + 1}
                        </strong>{" "}
                        ({timeFormatted}) —{" "}
                        <span
                          style={{
                            color: "var(--app-accent)",
                            fontWeight: "600",
                          }}
                        >
                          {displayDuration}
                        </span>
                      </>
                    ) : (
                      <>
                        {t("instance.ui.duration")}{" "}
                        <span
                          style={{
                            color: "var(--app-accent)",
                            fontWeight: "600",
                          }}
                        >
                          {displayDuration}
                        </span>
                      </>
                    )}
                  </span>

                  <Link
                    to={recordingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    state={{ fromCallList: true }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      color: "var(--app-accent)",
                      textDecoration: "none",
                      fontWeight: "600",
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("instance.ui.view")}
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={styles.callTile}
      style={{
        width: "100%",
        height: "auto",
        minHeight: "unset",
        overflow: "visible",
        backgroundColor: "var(--app-card)",
        border: "1px solid var(--app-border)",
        borderRadius: "12px",
        padding: "16px",
        boxSizing: "border-box",
        transition: "background-color 0.2s",
      }}
    >
      {!canJoin ? (
        <span
          className={classNames(styles.callTileLink, styles.disabled)}
          style={{
            width: "100%",
            display: "block",
            height: "auto",
            overflow: "visible",
          }}
        >
          {body}
        </span>
      ) : (
        <Link
          to={getRelativeRoomUrl(room.roomId, roomEncryptionSystem, room.name)}
          className={styles.callTileLink}
          style={{
            textDecoration: "none",
            color: "inherit",
            width: "100%",
            display: "block",
            height: "auto",
            overflow: "visible",
          }}
        >
          {body}
        </Link>
      )}
    </div>
  );
};
